/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl } from '../fetch-graphql';
import { UPDATE_CUSTOMER_ADDRESS } from './graphql/updateCustomerAddress.graphql';
import { handleNetworkError } from '@/account/lib/network-error';
import { handleFetchError } from '@/account/lib/fetch-error';
import { UpdateCustomerAddressResponse } from '@/account/types';
import { CustomerAddressesModel } from '@/account/data/models';
import { convertKeysCase } from '@/account/lib/convertCase';

type ExtendedAddressFormProps = CustomerAddressesModel & {
  addressId: number;
};

export const updateCustomerAddress = async (
  forms: ExtendedAddressFormProps
): Promise<string> => {
  const { addressId, ...address } = forms;

  if (!addressId) return '';

  return await fetchGraphQl(UPDATE_CUSTOMER_ADDRESS, {
    method: 'POST',
    variables: {
      id: Number(addressId),
      input: convertKeysCase(address, 'snakeCase', {
        custom_attributesV2: 'custom_attributesV2',
        firstName: 'firstname',
        lastName: 'lastname',
        middleName: 'middlename',
      }),
    },
  })
    .then((response: UpdateCustomerAddressResponse) => {
      if (response.errors?.length) return handleFetchError(response.errors);

      return response?.data?.updateCustomerAddress?.firstname || '';
    })
    .catch(handleNetworkError);
};
